import axios, { AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { getDevIdentity, isDevLoginEnabled } from '@/auth/devLogin';

const CORRELATION_HEADER = 'x-correlation-id';
const DEV_IDENTITY_HEADER = 'X-Dev-Identity';
const DEFAULT_TIMEOUT_MS = 30_000;

/** Error code on the synthetic failure raised for an HTML body on a JSON endpoint. */
export const ERR_HTML_FOR_JSON = 'ERR_HTML_FOR_JSON';

function contentTypeOf(response: AxiosResponse): string {
  const headers = response.headers as unknown as Record<string, unknown> | undefined;
  return String(headers?.['content-type'] ?? '');
}

function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
    timeout: DEFAULT_TIMEOUT_MS,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  instance.interceptors.request.use((config) => {
    config.headers.set(CORRELATION_HEADER, uuidv4());
    // Dev Login mode (localhost + Dev): tell the BE which synthetic identity is calling.
    // BE looks the id up in DevLoginIdentities and applies the matching oid + name + role.
    if (isDevLoginEnabled()) {
      const identity = getDevIdentity();
      if (identity) config.headers.set(DEV_IDENTITY_HEADER, identity.id);
    }
    return config;
  });

  // CloudFront's SPA fallback (web.yaml CustomErrorResponses) rewrites ANY 404 from the
  // API origin into `200` + index.html. CloudFront applies that mapping distribution-wide
  // and offers no way to scope it per cache behavior, so /api/* is caught by it too and
  // every API 404 reaches us as a SUCCESSFUL HTML response — tracked as B17.
  //
  // Left unguarded, callers destructure fields off that HTML body, read `undefined`, and
  // carry it into the next call. That is how a 404 on POST /workflows/{id}/upload-url
  // became a PUT to the literal path "undefined" and surfaced to the user as a CloudFront
  // 403, three hops from its cause (2026-08-14).
  //
  // A 2xx carrying HTML is unambiguous here: nothing behind apiClient returns HTML on
  // success (no endpoint sets responseType, every one is JSON), and genuine error pages —
  // WAF blocks, 5xx — keep their own status codes and are rejected by axios before this
  // runs. So the only way to land here is the 404 rewrite, which is why the synthetic
  // response restores status 404: that IS what the origin returned.
  //
  // Keep this even after B17 is fixed. It costs one header check and it is the difference
  // between a loud failure at the call site and a silent `undefined` three hops away.
  instance.interceptors.response.use((response) => {
    if (!contentTypeOf(response).includes('text/html')) return response;

    throw new AxiosError(
      `Expected JSON from ${response.config.url ?? 'the API'} but received an HTML page. ` +
        'The API returned 404 and CloudFront rewrote it to 200 + index.html (B17).',
      ERR_HTML_FOR_JSON,
      response.config,
      response.request,
      { ...response, status: 404, statusText: 'Not Found' },
    );
  });

  return instance;
}

export const apiClient = createApiClient();
